/**
 * A team's Settings, part by part (`?tab=settings&part=agents`): its planner, its agents in order, where they run, their
 * coding sessions, their source-control names, their identities and its versions. Each part reads the team as the
 * platform holds it (`GET /portfolio/v1/teams`) and a change saves at once through the team save (teamSave.ts), the
 * same merge the team editor uses; the whole record stays editable in Details › Edit.
 *
 * Environment, capabilities, side effects, integrations and the enclave are not parts here: a team carries no such
 * fields yet (docs/rbac.md proposes none), so there is nothing to show or keep.
 */

import { ORG_CONTEXT_PROFILES, type OrgTeamMemberV2, type OrgTeamV2 } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { ArrowDown, ArrowUp, Gear } from '@gravity-ui/icons';
import { Button, Tabs } from '@heroui/react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { routes } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { ChoiceSelect, ConfirmDialog, ErrorNote, StatusMark, Toggle } from '../ui.tsx';
import { ArtEmpty, type ArtTone } from '../ui/Art.tsx';
import { ListBlock, ListGroup, ListRow } from '../ui/Layout.tsx';
import { RowMenu } from '../ui/RowMenu.tsx';
import { MemberFace } from './MemberFace.tsx';
import { saveTeamChange, teamRecord } from './teamSave.ts';
import './overview.css';

export const SETTINGS_PARTS = [
  { id: 'planner', label: 'Planner' },
  { id: 'agents', label: 'Agents' },
  { id: 'placement', label: 'Where it runs' },
  { id: 'sessions', label: 'Coding sessions' },
  { id: 'source', label: 'Source control' },
  { id: 'identity', label: 'Identity' },
  { id: 'versions', label: 'Versions' },
] as const;
export type SettingsPart = (typeof SETTINGS_PARTS)[number]['id'];
export const isSettingsPart = (s: string | null | undefined): s is SettingsPart => SETTINGS_PARTS.some((p) => p.id === s);

const memberPath = (teamId: string, name: string) => `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(name)}`;

/** A member's role and the start of what it does: "Incident Analyst — performs triage and investigation…". */
function memberLine(m: OrgTeamMemberV2): string {
  const what = (m.agentInstructions ?? '').replace(/^#+\s.*$/gm, '').replace(/\s+/g, ' ').trim();
  const role = m.role ?? (m.human ? 'A person' : undefined);
  return [role, what].filter(Boolean).join(' — ') || (m.human ? 'A person' : 'No role set');
}

/** Where a member runs, in words. */
function placementWords(m: OrgTeamMemberV2, pools: string[]): string {
  const p = m.runsOn;
  if (m.human) return 'A person: given tasks, never started';
  const parts: string[] = [];
  if (p?.cloud) parts.push(`In the cloud (${(p.cloud as { provider?: string }).provider ?? 'a provider'})`);
  else if (p?.node) parts.push('On one node');
  else if (p?.device) parts.push("On one person's workbench");
  const tags = p?.tags ?? (pools.length ? pools : undefined);
  if (tags?.length) parts.push(`on nodes tagged ${tags.join(', ')}`);
  if (p?.sandbox) parts.push('in a sandbox');
  if (p?.fallback) parts.push(p.fallback === 'local' ? 'else on the asking computer' : p.fallback === 'wait' ? 'else it waits' : 'else it fails');
  if (!parts.length) return "On the computer of whoever starts it";
  const s = parts.join(', ');
  return s[0]!.toUpperCase() + s.slice(1);
}

const CLEANUP_WORDS: Record<'auto' | 'manual' | 'prompt', string> = { auto: 'Clean up when merged', prompt: 'Ask each time', manual: 'Leave for a person' };
const REVIEW_WORDS: Record<string, string> = { none: 'No review', self: 'The agent reviews its own work', human: 'A person reviews' };
const PERMISSION_WORDS: Record<string, string> = { supervised: 'Asks before each change', 'auto-edits': 'Edits on its own, asks for the rest', auto: 'Works on its own, asks for risky steps', full: 'Works on its own' };

function Face({ m }: { m: OrgTeamMemberV2 }) {
  return <MemberFace member={m} />;
}

/** The designated planner: who leads, with Clear for those who may. */
function PlannerCard({ team, mayEdit, busy, onClear }: { team: OrgTeamV2; mayEdit: boolean; busy: boolean; onClear: () => void }) {
  const lead = team.members.find((m) => m.name === team.lead);
  return (
    <ListGroup
      title="Designated planner"
      description="Leads the team: plans the work and routes each part to the agent best placed for it."
      actions={mayEdit && lead ? <Button size="sm" variant="tertiary" isDisabled={busy} onPress={onClear}>Clear</Button> : undefined}
    >
      {lead ? (
        <ListBlock>
          <div className="team-member-row">
            <Face m={lead} />
            <span className="team-member-row__text">
              <Link to={memberPath(team.id, lead.name)} className="text-sm font-medium hover:underline">{lead.name}</Link>
              <span className="team-member-row__desc">{lead.role ?? 'No role set'}</span>
            </span>
          </div>
        </ListBlock>
      ) : (
        <ListBlock>
          <p className="text-sm text-muted">No planner. Each task goes to the agent it names; choose one with Make planner on the Agents part.</p>
        </ListBlock>
      )}
    </ListGroup>
  );
}

function Value({ children }: { children: ReactNode }) {
  return <span className="text-sm text-muted">{children}</span>;
}

export function TeamSettings({ teamId, kind, part, onPart, mayEdit, tone, onChanged }: { teamId: string; kind: 'team' | 'specialist'; part: SettingsPart; onPart: (p: SettingsPart) => void; mayEdit: boolean; tone: ArtTone; onChanged: () => void }) {
  const navigate = useNavigate();
  const rec = useLoad(() => teamRecord(teamId, kind), [teamId, kind]);
  const history = useLoad(() => (part === 'versions' ? routes.teamHistory(teamId) : Promise.resolve(undefined)), [teamId, part, rec.data?.version]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [removing, setRemoving] = useState<OrgTeamMemberV2>();
  const team = rec.data;

  const change = async (fn: Parameters<typeof saveTeamChange>[2]) => {
    setBusy(true);
    setError(undefined);
    try {
      const notes = await saveTeamChange(teamId, kind, fn);
      if (notes.length) setError(notes.join(' '));
      rec.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const move = (name: string, by: -1 | 1) =>
    change((d) => {
      const i = d.members.findIndex((m) => m.name === name);
      const j = i + by;
      if (i < 0 || j < 0 || j >= d.members.length) return d;
      const members = [...d.members];
      [members[i], members[j]] = [members[j]!, members[i]!];
      return { ...d, members };
    });
  const setMember = (name: string, patch: Partial<OrgTeamMemberV2>) => change((d) => ({ ...d, members: d.members.map((m) => (m.name === name ? { ...m, ...patch } : m)) }));

  const parts = kind === 'specialist' ? SETTINGS_PARTS.filter((p) => p.id !== 'planner' && p.id !== 'agents') : SETTINGS_PARTS;
  const shown = parts.some((p) => p.id === part) ? part : parts[0]!.id;

  const body = (() => {
    if (!team) return null;
    const agents = team.members;
    const pools = team.grants.pools;
    switch (shown) {
      case 'planner':
        return (
          <>
            <PlannerCard team={team} mayEdit={mayEdit} busy={busy} onClear={() => void change((d) => ({ ...d, lead: '' }))} />
            <ListGroup title="How it plans" description="Set in the team's editor (Details, Edit); the workbench applies them when the planner runs.">
              <ListRow title="Plans at once" value={<Value>{team.settings?.maxConcurrentPlans ?? 'Not set'}</Value>} />
              <ListRow title="Review" value={<Value>{team.settings?.reviewPolicy ? REVIEW_WORDS[team.settings.reviewPolicy] : 'Not set'}</Value>} />
              <ListRow title="Token budget per task" value={<Value>{team.settings?.taskTokenBudget ? team.settings.taskTokenBudget.toLocaleString() : 'Not set'}</Value>} />
              <ListRow title="Guidelines" description={team.settings?.guidelines ?? 'None written.'} />
            </ListGroup>
          </>
        );
      case 'agents':
        return (
          <>
            <PlannerCard team={team} mayEdit={mayEdit} busy={busy} onClear={() => void change((d) => ({ ...d, lead: '' }))} />
            <ListGroup title="Members" count={agents.length} description="In the order the team lists them. The planner leads; the rest take the work it routes to them.">
              {agents.map((m, i) => (
                <ListBlock key={m.name}>
                  <div className="team-member-row">
                    <Face m={m} />
                    <span className="team-member-row__text">
                      <span className="flex min-w-0 items-baseline gap-2">
                        {m.human ? <span className="truncate text-sm font-medium">{m.name}</span> : <Link to={memberPath(team.id, m.name)} className="truncate text-sm font-medium hover:underline">{m.name}</Link>}
                        {team.lead === m.name ? <span className="shrink-0 text-xs text-muted">Planner</span> : null}
                      </span>
                      <span className="team-member-row__desc" title={memberLine(m)}>{memberLine(m)}</span>
                    </span>
                    {mayEdit ? (
                      <span className="team-member-row__controls">
                        <Button size="sm" variant="ghost" isIconOnly aria-label={`Move ${m.name} up`} isDisabled={busy || i === 0} onPress={() => void move(m.name, -1)}>
                          <ArrowUp className="size-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" isIconOnly aria-label={`Move ${m.name} down`} isDisabled={busy || i === agents.length - 1} onPress={() => void move(m.name, 1)}>
                          <ArrowDown className="size-3.5" />
                        </Button>
                        <RowMenu
                          label={`More for ${m.name}`}
                          items={[
                            team.lead !== m.name && { id: 'planner', label: 'Make planner' },
                            !m.human && { id: 'open', label: 'Open overview' },
                            agents.length > 1 && { id: 'remove', label: 'Remove from the team', danger: true },
                          ]}
                          onAction={(id) => {
                            if (id === 'planner') void change((d) => ({ ...d, lead: m.name }));
                            else if (id === 'open') navigate(memberPath(team.id, m.name));
                            else if (id === 'remove') setRemoving(m);
                          }}
                        />
                      </span>
                    ) : null}
                  </div>
                </ListBlock>
              ))}
            </ListGroup>
          </>
        );
      case 'placement':
        return (
          <ListGroup title="Where it runs" description={pools.length ? `The team's agents may run on nodes tagged ${pools.join(', ')}, unless a member says otherwise.` : 'Each member runs where its placement says; with none, on the computer of whoever starts it.'}>
            {agents.map((m) => <ListRow key={m.name} title={m.name} description={placementWords(m, pools)} />)}
          </ListGroup>
        );
      case 'sessions': {
        const cleanup = team.settings?.worktreeCleanup;
        return (
          <>
            <ListGroup title="Coding sessions" description="How the team's agents work on code: in a worktree of their own, and what happens to it after.">
              <ListRow
                title="Worktree clean-up"
                description="When an agent's worktree is finished with."
                control={mayEdit ? <ChoiceSelect label="Worktree clean-up" value={cleanup} placeholder="Not set" options={CLEANUP_WORDS} isDisabled={busy} onChange={(v) => void change((d) => ({ ...d, settings: { ...d.settings, worktreeCleanup: v } }))} /> : <Value>{cleanup ? CLEANUP_WORDS[cleanup] : 'Not set'}</Value>}
              />
              <ListRow title="Permission mode" description="Changed in the team's editor." value={<Value>{team.settings?.permissionMode ? PERMISSION_WORDS[team.settings.permissionMode] : 'Each member’s own'}</Value>} />
              <ListRow title="Context budget" value={<Value>{team.contextProfile && team.contextProfile !== 'auto' && (ORG_CONTEXT_PROFILES as readonly string[]).includes(team.contextProfile) ? `${team.contextProfile[0]!.toUpperCase()}${team.contextProfile.slice(1)}` : 'Chosen per member'}</Value>} />
            </ListGroup>
            <ListGroup title="Own worktree" description="An agent with its own worktree works on a branch of its own, so two agents never edit the same files at once.">
              {agents.filter((m) => !m.human).map((m) => (
                <ListRow key={m.name} title={m.name} description={m.useWorktree ? 'Works in a worktree of its own' : 'Works in the shared checkout'} control={<Toggle label={`${m.name} works in its own worktree`} checked={!!m.useWorktree} isDisabled={!mayEdit || busy} onChange={(v) => void setMember(m.name, { useWorktree: v })} />} />
              ))}
            </ListGroup>
          </>
        );
      }
      case 'source':
        return (
          <ListGroup title="Source control" description="The names each member works under in GitHub and Azure DevOps, so their commits and pull requests are theirs.">
            {agents.map((m) => (
              <ListRow key={m.name} title={m.name} description={[m.githubLogin ? `GitHub: ${m.githubLogin}` : undefined, m.azureDevOpsUser ? `Azure DevOps: ${m.azureDevOpsUser.displayName ?? m.azureDevOpsUser.uniqueName}` : undefined].filter(Boolean).join(' · ') || 'Works under the name of whoever starts it'} />
            ))}
          </ListGroup>
        );
      case 'identity':
        return (
          <ListGroup title="Identity" description={`Each agent's Microsoft agent identity, and the person accountable for it.${team.owner ? ` ${team.owner.name ?? 'Its owner'} owns the team and sponsors its identities.` : ''}`}>
            {agents.filter((m) => !m.human).map((m) => (
              <ListRow
                key={m.name}
                title={m.name}
                description={m.entraAgentId ? `${m.entraAgentId}${m.sponsor ? ` · sponsor ${m.sponsor}` : ''}` : m.sponsor ? `Sponsor ${m.sponsor}` : 'Made when your organisation connects Agent 365'}
                status={m.entraAgentId ? <StatusMark state="online" label="Has an identity" /> : <StatusMark state="idle" label="No identity yet" />}
              />
            ))}
          </ListGroup>
        );
      case 'versions':
        return (
          <ListGroup title="Versions" count={history.data?.versions.length} description={history.data?.note ?? `Version ${team.version}, changed ${timeAgo(team.updatedAt)}.`}>
            {history.error ? <ListBlock><ErrorNote error={history.error} /></ListBlock> : null}
            {(history.data?.versions ?? []).slice(0, 50).map((v, i) => (
              <ListRow
                key={`${v.at}-${i}`}
                title={`${v.version !== undefined ? `Version ${v.version}: ` : ''}${v.action.replace(/^team\./, '').replace(/\./g, ' ')}`}
                description={`${dateTime(v.at)} · ${v.byName ?? v.by ?? 'the platform'}${v.paths.length ? ` · ${v.paths.slice(0, 4).join(', ')}${v.paths.length > 4 ? ` and ${v.paths.length - 4} more` : ''}` : ''}`}
              />
            ))}
            {history.data && !history.data.versions.length ? <ListBlock><p className="text-sm text-muted">No history recorded yet.</p></ListBlock> : null}
          </ListGroup>
        );
    }
  })();

  return (
    <div className="grid gap-4">
      <div className="-m-1 max-w-full overflow-x-auto p-1">
        <Tabs variant="secondary" selectedKey={shown} onSelectionChange={(k) => onPart(String(k) as SettingsPart)} className="w-fit min-w-max">
          <Tabs.ListContainer>
            <Tabs.List aria-label="Settings: which part">
              {parts.map((p) => (
                <Tabs.Tab key={p.id} id={p.id} className="whitespace-nowrap">
                  {p.label}
                  <Tabs.Indicator />
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>
      </div>
      <ErrorNote error={error} />
      {rec.error ? (
        <ArtEmpty tone={tone} icon={<Gear />} title="Its settings are shown to portfolio managers">
          Administrators and portfolio managers see and change a team's settings here. Its overview and its check are open to everyone on its projects.
        </ArtEmpty>
      ) : !team ? (
        rec.loading ? <p className="text-sm text-muted">Reading the team…</p> : <ArtEmpty tone={tone} icon={<Gear />} title="No team by that name">It may have been removed.</ArtEmpty>
      ) : (
        body
      )}
      {removing ? (
        <ConfirmDialog
          isOpen
          title={`Remove ${removing.name} from ${team?.name ?? 'the team'}?`}
          confirmLabel="Remove"
          danger
          onCancel={() => setRemoving(undefined)}
          onConfirm={async () => {
            const name = removing.name;
            setRemoving(undefined);
            await change((d) => ({ ...d, lead: d.lead === name ? '' : d.lead, members: d.members.filter((m) => m.name !== name) }));
          }}
        >
          <p>{removing.name} stops taking the team's work on every machine at its next refresh. Its history stays; work already assigned to it waits for someone to take it.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/** When the version changed, said where a header needs it. */
export const versionWords = (v: number, at?: string) => `version ${v}${at ? `, ${timeAgo(at)}` : ''}`;
